import React, { useEffect, useState, useRef } from 'react';
import { getUsuarioStorage } from '../helpers/getUsuarioStorage';
import { setFavicon } from '../helpers/setFavicon';
import { serverApi } from '../api';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { EmpresaFormFields, normalizarVoz } from '../components/EmpresaFormFields';
import { guardarColoresStorage } from '../helpers/theme';

const MySwal = withReactContent(Swal);

export const MiEmpresaPage = () => {
    const usuarioRef = useRef(getUsuarioStorage());
    const [empresa, setEmpresa] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(null);

    useEffect(() => {
        const usuario = usuarioRef.current;
        if (!usuario?.codigoEntidad) { setLoading(false); return; }
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const resp = await serverApi.get(`/superadmin/entidades/${usuario.codigoEntidad}`);
                if (!cancelled && resp.data.ok) {
                    const e = resp.data.entidad;
                    setEmpresa(e);
                    setForm({
                        codigoEntidad: e.codigoEntidad || '',
                        nombreEntidad: e.nombreEntidad || '',
                        nit: e.nit || '',
                        imagenLogo: e.imagenLogo || '',
                        imagenIcono: e.imagenIcono || '',
                        llamadoVoz: e.llamadoVoz ?? 0,
                        voz: normalizarVoz(e.voz),
                        textoMarquee: e.textoMarquee || '',
                        colorPrimario: e.colorPrimario || 'var(--color-primario)',
                        colorSecundario: e.colorSecundario || 'var(--color-secundario)',
                        telefonoContacto: e.telefonoContacto || '',
                        emailContacto: e.emailContacto || '',
                        direccion: e.direccion || '',
                        ciudad: e.ciudad || '',
                        maxSedes: e.maxSedes || 1,
                        indActivo: e.indActivo ?? 1,
                    });
                }
            } catch (err) {
                console.error('Error al cargar datos de mi empresa:', err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const handleChange = (e) => {
        const { id, value, type } = e.target;
        setForm(prev => ({
            ...prev,
            [id]: type === 'number' || id === 'llamadoVoz' || id === 'maxSedes' || id === 'indActivo'
                ? Number(value) : value,
        }));
    };

    const handleImageChange = (changes) => {
        setForm(prev => ({ ...prev, ...changes }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.nombreEntidad) return MySwal.fire('Atención', 'El nombre de la empresa es obligatorio', 'warning');
        try {
            setSaving(true);
            const resp = await serverApi.put('/superadmin/entidades', form);
            if (resp.data.ok) {
                const rawUser = localStorage.getItem('usuario');
                if (rawUser) {
                    try {
                        const parsed = JSON.parse(rawUser);
                        if (parsed.codigoEntidad === form.codigoEntidad) {
                            localStorage.setItem('usuario', JSON.stringify({
                                ...parsed,
                                nombreEntidad: form.nombreEntidad,
                                imagenLogo: form.imagenLogo,
                                imagenIcono: form.imagenIcono,
                                llamadoVoz: form.llamadoVoz,
                            }));
                            setFavicon(form.imagenIcono || form.imagenLogo);
                        }
                    } catch (_) { }
                }
                // Aplicar el tema de inmediato para el usuario actual
                guardarColoresStorage(form.colorPrimario, form.colorSecundario);
                setEmpresa(prev => ({ ...prev, ...form }));
                MySwal.fire({ title: '¡Empresa Actualizada!', text: resp.data.msg, icon: 'success', timer: 1800, showConfirmButton: false });
            }
        } catch (error) {
            MySwal.fire('Error', error.response?.data?.msg || 'Error al actualizar', 'error');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div style={{ padding: 40, textAlign: 'center', color: '#64748b', fontFamily: "'Inter', sans-serif" }}>
                <span className="spinner-border" style={{ width: 32, height: 32, marginBottom: 12, color: 'var(--color-primario-texto)' }} role="status"></span>
                <p>Cargando información de la empresa...</p>
            </div>
        );
    }

    if (!empresa || !form) {
        return (
            <div style={{ padding: 40, textAlign: 'center', color: '#ef4444', fontFamily: "'Inter', sans-serif" }}>
                <h4>No se encontró información de la empresa</h4>
                <p>Verifique que su usuario pertenezca a una entidad válida.</p>
            </div>
        );
    }

    return (
        <div style={{ padding: '24px 28px', maxWidth: 900, margin: '0 auto', fontFamily: "'Inter', sans-serif" }}>
            {/* Header */}
            <div style={{
                background: 'linear-gradient(135deg, var(--color-primario) 0%, var(--color-secundario) 100%)',
                borderRadius: 20, padding: '24px 28px', color: 'var(--color-marca-contraste)',
                boxShadow: '0 10px 30px rgba(var(--color-primario-rgb),0.2)', marginBottom: 28,
                display: 'flex', alignItems: 'center', gap: 16,
            }}>
                <span style={{ fontSize: 36 }}>🏢</span>
                <div>
                    <h3 style={{ margin: 0, fontWeight: 800, fontSize: 22 }}>Configuración de Mi Empresa</h3>
                    <p style={{ margin: '4px 0 0', opacity: 0.85, fontSize: 13 }}>
                        Edite la información institucional de <b>{empresa.nombreEntidad}</b>
                    </p>
                </div>
            </div>

            {/* Formulario inline */}
            <div style={{
                background: '#ffffff', borderRadius: 20,
                boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                border: '1px solid #e2e8f0', padding: '28px 32px',
            }}>
                <form onSubmit={handleSubmit}>
                    <EmpresaFormFields
                        form={form}
                        onChange={handleChange}
                        onImageChange={handleImageChange}
                        isSuperAdmin={false}
                        readonlyCodigo={true}
                        accentColor="var(--color-primario)"
                    />

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                        <button
                            type="submit"
                            disabled={saving}
                            style={{
                                padding: '13px 32px', borderRadius: 12, border: 'none',
                                background: saving ? '#94a3b8' : 'linear-gradient(135deg, var(--color-primario) 0%, var(--color-secundario) 100%)',
                                color: 'var(--color-marca-contraste)', fontWeight: 800, fontSize: 14,
                                cursor: saving ? 'not-allowed' : 'pointer',
                                boxShadow: saving ? 'none' : '0 4px 14px rgba(var(--color-primario-rgb),0.35)',
                                transition: 'all 0.2s',
                            }}
                        >
                            {saving
                                ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Guardando...</>
                                : '💾 GUARDAR CAMBIOS'
                            }
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default MiEmpresaPage;
